import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  AlertCircle,
  Package,
  Search,
  ChevronLeft,
  ChevronRight,
  RotateCw,
} from "lucide-react";
import { useCustomerContext } from "@/context/CustomerContext";
import StatusBadge from "@/components/common/StatusBadge";
import { getClaimTypeLabel, isShippingClaimType } from "@/utils/claimType";

const ITEMS_PER_PAGE = 10;

const Claims: React.FC = () => {
  const { claims, isLoading, refreshData } = useCustomerContext();

  const [activeTab, setActiveTab] = useState<"all" | "product" | "shipping">(
    "all",
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  // --- Filter Logic ---
  const filteredClaims = claims.filter((c) => {
    const typeLower = (c.claimType || "").toLowerCase();
    const nameLower = (c.productName || "").toLowerCase();
    const searchLower = searchQuery.toLowerCase();

    // 1. Tab Filtering
    let matchesTab = false;
    if (activeTab === "all") {
      matchesTab = true;
    } else if (activeTab === "product") {
      matchesTab = !isShippingClaimType(c.claimType);
    } else if (activeTab === "shipping") {
      matchesTab = isShippingClaimType(c.claimType);
    }

    // 2. Search Filtering
    const matchesSearch =
      nameLower.includes(searchLower) ||
      typeLower.includes(searchLower) ||
      getClaimTypeLabel(c.claimType).toLowerCase().includes(searchLower);

    return matchesTab && matchesSearch;
  });

  // --- Pagination Logic ---
  const totalPages = Math.ceil(filteredClaims.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedClaims = filteredClaims.slice(
    startIndex,
    startIndex + ITEMS_PER_PAGE,
  );

  const handleTabChange = (tab: "all" | "product" | "shipping") => {
    setActiveTab(tab);
    setCurrentPage(1); // Reset to page 1 on tab change
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1); // Reset to page 1 on search
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  // --- Animation Variants ---
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.1 } },
  };
  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: "spring", stiffness: 100, damping: 15 },
    },
  };

  if (isLoading && claims.length === 0) {
    return (
      <div className="tw-min-h-screen tw-flex tw-justify-center tw-items-center tw-h-64">
        <div className="tw-w-12 tw-h-12 tw-border-t-4 tw-border-[#00C5FB] tw-border-solid tw-rounded-full tw-animate-spin" />
      </div>
    );
  }

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={containerVariants}
      className="tw-space-y-6 tw-bg-[#202020] tw-min-h-screen tw-p-2 md:tw-p-6"
    >
      {/* Header */}
      <motion.div
        variants={itemVariants}
        className="tw-flex md:tw-flex-row tw-flex-col tw-justify-between tw-items-start tw-p-5 md:tw-p-0 tw-gap-3"
      >
        <div className="md:tw-w-auto">
          <h1 className="tw-text-2xl tw-font-bold tw-text-gray-100">
            My Claims
          </h1>
          <p className="tw-text-gray-300">
            Track and manage your insurance claims
          </p>
        </div>
        <Link
          to="/customer/chat"
          className="tw-bg-[#00C5FB] hover:tw-bg-cyan-500 tw-text-gray-900 tw-font-medium tw-px-4 tw-py-2 tw-rounded-lg"
        >
          File a claim
        </Link>
      </motion.div>

      {/* Filter & Search */}
      <motion.div
        variants={itemVariants}
        className="tw-bg-[#202020] tw-rounded-lg tw-overflow-hidden"
      >
        <div className="tw-flex tw-flex-col lg:tw-flex-row tw-items-center tw-justify-between tw-px-6 tw-py-4 tw-gap-4">
          {/* Search input */}
          <div className="tw-flex tw-w-full lg:tw-flex-1 tw-gap-2">
            <div className="tw-relative tw-flex-1 lg:tw-max-w-[256px]">
              <Search className="tw-absolute tw-left-3 tw-top-1/2 tw-transform tw--translate-y-1/2 tw-text-gray-500" />
              <input
                type="text"
                placeholder="Search claims by name or type"
                value={searchQuery}
                onChange={handleSearchChange}
                className="
                  tw-block tw-w-full tw-pl-10 tw-pr-3 tw-py-2
                  tw-bg-[#1C1C1E] tw-border tw-border-gray-700 tw-rounded-md
                  tw-placeholder-gray-500 focus:tw-outline-none focus:tw-ring-cyan-500 focus:tw-border-cyan-500
                  sm:tw-text-sm tw-text-gray-100
                "
              />
            </div>
            <button
              onClick={() => refreshData()}
              disabled={isLoading}
              className={`tw-p-2 tw-bg-[#1c1c1e] tw-border tw-border-gray-700 tw-rounded-md tw-text-gray-400 hover:tw-text-[#00C5FB] hover:tw-border-[#00C5FB] tw-transition-all ${isLoading ? "tw-opacity-50 tw-cursor-not-allowed" : ""}`}
              title="Refresh Claims"
            >
              <RotateCw className={`tw-w-5 tw-h-5 ${isLoading ? "tw-animate-spin" : ""}`} />
            </button>
          </div>

          {/* Tabs */}
          <div className="tw-flex tw-flex-wrap tw-space-x-4">
            {(["all", "product", "shipping"] as const).map((tab) => (
              <button
                key={tab}
                onClick={() => handleTabChange(tab)}
                className={`
                  md:tw-px-4 tw-px-2 tw-py-2 tw-text-xs md:tw-text-sm tw-font-medium tw-rounded-lg tw-transition-colors
                  ${
                    activeTab === tab
                      ? "tw-bg-[#2C2C2E] tw-text-[#00C5FB]"
                      : "tw-text-gray-400 hover:tw-text-cyan-300 hover:tw-bg-[#2C2C2E]"
                  }
                `}
              >
                {tab === "all"
                  ? "All Claims"
                  : tab === "product"
                    ? "Product Claims"
                    : "Shipping Claims"}
              </button>
            ))}
          </div>
        </div>

        {/* Claims list */}
        {filteredClaims.length > 0 ? (
          <>
            <div className="tw-grid tw-grid-cols-1 tw-gap-6 tw-py-4">
              {paginatedClaims.map((claim) => (
                <Link
                  to={`/customer/claims/${claim.id}`}
                  state={{ claim }}
                  key={claim.id}
                  className="tw-block"
                >
                  <ClaimCard claim={claim} />
                </Link>
              ))}
            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="tw-flex tw-justify-between tw-items-center tw-px-6 tw-py-4 tw-border-t tw-border-gray-800">
                <p className="tw-text-sm tw-text-gray-400">
                  Showing {startIndex + 1} to{" "}
                  {Math.min(startIndex + ITEMS_PER_PAGE, filteredClaims.length)}{" "}
                  of {filteredClaims.length} entries
                </p>
                <div className="tw-flex tw-space-x-2">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className={`tw-p-2 tw-rounded-md tw-border tw-border-gray-700 ${
                      currentPage === 1
                        ? "tw-text-gray-600 tw-cursor-not-allowed"
                        : "tw-text-gray-300 hover:tw-bg-gray-800"
                    }`}
                  >
                    <ChevronLeft className="tw-w-4 tw-h-4" />
                  </button>
                  <span className="tw-px-4 tw-py-2 tw-text-sm tw-text-gray-300">
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className={`tw-p-2 tw-rounded-md tw-border tw-border-gray-700 ${
                      currentPage === totalPages
                        ? "tw-text-gray-600 tw-cursor-not-allowed"
                        : "tw-text-gray-300 hover:tw-bg-gray-800"
                    }`}
                  >
                    <ChevronRight className="tw-w-4 tw-h-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-16">
            <AlertCircle className="tw-w-12 tw-h-12 tw-text-gray-500" />
            <h3 className="tw-mt-2 tw-text-sm tw-font-medium tw-text-gray-100">
              No claims found
            </h3>
            <p className="tw-mt-1 tw-text-sm tw-text-gray-400">
              {searchQuery
                ? `No claims matching "${searchQuery}"`
                : activeTab === "all"
                  ? "You haven't filed any claims yet."
                  : activeTab === "product"
                    ? "No product claims filed yet."
                    : "No shipping claims filed yet."}
            </p>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
};

interface ClaimCardProps {
  claim: {
    id: string;
    productName: string;
    protection: string;
    status: string;
    amount: number;
    imageUrl?: string;
    claimType: string;
  };
}

export const ClaimCard: React.FC<ClaimCardProps> = ({ claim }) => {
  const protectionLabel = getClaimTypeLabel(claim.claimType);

  return (
    <div className="tw-relative tw-bg-[#202020] tw-border tw-border-gray-700 tw-rounded-xl tw-px-6 tw-py-6 tw-flex tw-flex-col lg:tw-flex-row-reverse lg:tw-justify-between lg:tw-items-start tw-gap-4 lg:tw-gap-4">
      <span className="tw-absolute tw--top-3 tw-left-6 tw-bg-[#202020] tw-text-sm tw-font-semibold tw-text-gray-400 tw-px-2 tw-py-0.5 tw-rounded lg:tw-col-span-full">
        {protectionLabel}
      </span>

      <div className="tw-flex tw-justify-end lg:tw-col-start-2 lg:tw-row-start-1">
        <StatusBadge status={claim.status as any} size="md" />
      </div>

      <div className="tw-flex tw-gap-4 tw-items-start tw-justify-start lg:tw-col-span-2 lg:tw-row-start-2 lg:tw-grid lg:tw-grid-cols-[auto,1fr] lg:tw-gap-4">
        <div className="tw-w-20 tw-h-20 tw-self-center tw-flex-shrink-0">
          {claim.imageUrl ? (
            <img
              src={claim.imageUrl}
              alt={claim.productName}
              className="tw-w-full tw-h-full tw-object-contain tw-rounded-md"
            />
          ) : (
            <div className="tw-w-full tw-h-full tw-bg-gray-700 tw-flex tw-items-center tw-justify-center tw-rounded-md">
              <Package className="tw-w-8 tw-h-8 tw-text-gray-500" />
            </div>
          )}
        </div>
        <div className="tw-flex-1 tw-space-y-1">
          <h3 className="tw-text-lg tw-font-medium tw-text-gray-100">
            {claim.productName}
          </h3>
          <p className="tw-text-sm">
            <span className="tw-text-gray-400">Claim ID:</span>{" "}
            <span className="tw-text-gray-100">CLM-{claim.id.slice(-6)}</span>
          </p>
          <p className="tw-text-sm">
            <span className="tw-text-gray-400">Type:</span>{" "}
            <span className="tw-text-gray-100">{protectionLabel}</span>
          </p>
          <p className="tw-text-sm">
            <span className="tw-text-gray-400">Claim Amount:</span>{" "}
            <span className="tw-text-gray-100">
              {claim.amount != null && claim.amount > 0
                ? `₹${claim.amount.toLocaleString()}`
                : "Not decided yet"}
            </span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Claims;
